/**
 * TanStack Query hooks for the admin Finance feature (ADM-29/30/31/33).
 *
 * Ergonomics mirror `features/payments-sandbox/hooks.ts`: a query-key factory,
 * mutations invalidate the relevant keys on success. Toasts are left to the
 * calling page (each screen surfaces its own copy — e.g. payout-now needs a
 * per-error-code message, not a generic one).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { adminDelete, adminGet, adminPost, adminPut } from './api';
import type {
  AdminFinanceSummaryResponse,
  AdminMerchantFinanceResponse,
  AdminPayoutNowResponse,
  AnticipationTermDto,
  AnticipationTermsResponse,
  DeliveryFeeTierDto,
  FeeConfigRequest,
  FeeConfigResponse,
  MerchantSearchItem,
} from './types';

const GLOBAL = 'global';

export const financeKeys = {
  all: ['finance'] as const,
  feeConfig: (merchantId?: string) => ['finance', 'fee-config', merchantId ?? GLOBAL] as const,
  anticipation: (merchantId?: string) =>
    ['finance', 'anticipation-options', merchantId ?? GLOBAL] as const,
  deliveryTiers: () => ['finance', 'delivery-fee-tiers'] as const,
  summary: () => ['finance', 'summary'] as const,
  merchant: (merchantId: string, page: number, size: number) =>
    ['finance', 'merchant', merchantId, page, size] as const,
  merchantSearch: (query: string) => ['finance', 'merchant-search', query] as const,
};

// ---- ADM-29: fee config -----------------------------------------------------

export function useFeeConfig(merchantId?: string): UseQueryResult<FeeConfigResponse, Error> {
  return useQuery({
    queryKey: financeKeys.feeConfig(merchantId),
    queryFn: () =>
      adminGet<FeeConfigResponse>(
        merchantId ? `/finance/fee-config/${merchantId}` : '/finance/fee-config',
      ),
  });
}

export function useSaveFeeConfig(
  merchantId?: string,
): UseMutationResult<FeeConfigResponse, Error, FeeConfigRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      adminPut<FeeConfigResponse, FeeConfigRequest>(
        merchantId ? `/finance/fee-config/${merchantId}` : '/finance/fee-config',
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.feeConfig(merchantId) });
      if (merchantId) void queryClient.invalidateQueries({ queryKey: financeKeys.feeConfig() });
    },
  });
}

export function useDeleteFeeConfigOverride(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (merchantId) => adminDelete(`/finance/fee-config/${merchantId}`),
    onSuccess: (_data, merchantId) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.feeConfig(merchantId) });
    },
  });
}

// ---- ADM-30: anticipation options -------------------------------------------

export function useAnticipationOptions(
  merchantId?: string,
): UseQueryResult<AnticipationTermsResponse, Error> {
  return useQuery({
    queryKey: financeKeys.anticipation(merchantId),
    queryFn: () =>
      adminGet<AnticipationTermsResponse>(
        merchantId
          ? `/finance/anticipation-options/${merchantId}`
          : '/finance/anticipation-options',
      ),
  });
}

export function useSaveAnticipationOptions(
  merchantId?: string,
): UseMutationResult<AnticipationTermsResponse, Error, AnticipationTermDto[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options) =>
      adminPut<AnticipationTermsResponse, { options: AnticipationTermDto[] }>(
        merchantId
          ? `/finance/anticipation-options/${merchantId}`
          : '/finance/anticipation-options',
        { options },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.anticipation(merchantId) });
      if (merchantId) void queryClient.invalidateQueries({ queryKey: financeKeys.anticipation() });
    },
  });
}

export function useDeleteAnticipationOverride(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (merchantId) => adminDelete(`/finance/anticipation-options/${merchantId}`),
    onSuccess: (_data, merchantId) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.anticipation(merchantId) });
    },
  });
}

// ---- ADM-31: delivery fee tiers ----------------------------------------------

export function useDeliveryFeeTiers(): UseQueryResult<DeliveryFeeTierDto[], Error> {
  return useQuery({
    queryKey: financeKeys.deliveryTiers(),
    queryFn: () => adminGet<DeliveryFeeTierDto[]>('/finance/delivery-fee-tiers'),
  });
}

export function useSaveDeliveryFeeTiers(): UseMutationResult<
  DeliveryFeeTierDto[],
  Error,
  DeliveryFeeTierDto[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tiers) =>
      adminPut<DeliveryFeeTierDto[], DeliveryFeeTierDto[]>('/finance/delivery-fee-tiers', tiers),
    onSuccess: (data) => {
      queryClient.setQueryData(financeKeys.deliveryTiers(), data);
    },
  });
}

// ---- ADM-33: platform summary + per-merchant finance + payout-now -----------

export function useFinanceSummary(): UseQueryResult<AdminFinanceSummaryResponse, Error> {
  return useQuery({
    queryKey: financeKeys.summary(),
    queryFn: () => adminGet<AdminFinanceSummaryResponse>('/finance/summary'),
  });
}

export function useMerchantFinance(
  merchantId: string | null,
  page: number,
  size: number,
): UseQueryResult<AdminMerchantFinanceResponse, Error> {
  return useQuery({
    queryKey: financeKeys.merchant(merchantId ?? '', page, size),
    queryFn: () =>
      adminGet<AdminMerchantFinanceResponse>(`/finance/merchants/${merchantId}`, { page, size }),
    enabled: merchantId !== null,
  });
}

export function usePayoutNow(
  merchantId: string | null,
): UseMutationResult<AdminPayoutNowResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adminPost<AdminPayoutNowResponse, Record<string, never>>(
      `/finance/merchants/${merchantId}/payout-now`,
      {},
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.summary() });
      if (merchantId) {
        void queryClient.invalidateQueries({ queryKey: ['finance', 'merchant', merchantId] });
      }
    },
  });
}

// ---- shared merchant search picker (fee-config / anticipation / finance) ----

/** Only fires once `query` has 2+ chars (mirrors the backend's own guard). */
export function useMerchantSearch(query: string): UseQueryResult<MerchantSearchItem[], Error> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: financeKeys.merchantSearch(trimmed),
    queryFn: () => adminGet<MerchantSearchItem[]>('/merchants/search', { q: trimmed, size: 20 }),
    enabled: trimmed.length >= 2,
  });
}
